import {
  authCookieLoginAuthLoginPostMutation,
  authCookieLogoutAuthLogoutPostMutation,
  getOrganizationInfoAppOrganizationOrganizationsInfoGetOptions,
  getSuperuserImpersonationStatusAuthSuperuserImpersonationStatusGetOptions,
  magicLinkCallbackAuthMagicLinkCallbackPostMutation,
  usersCurrentUserAuthUsersMeGetOptions,
} from '@/client/@tanstack/react-query.gen'
import type { SuperuserImpersonationStatus, UserlandBaseUser } from '@/client/types.gen'
import { AuthContext } from '@/hooks/use-auth'
import type { AuthState } from '@/hooks/use-auth'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { posthog } from 'posthog-js'
import { router } from '../router'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { __setActingUserIdInternal } from '@/lib/acting-user'
import { SESSION_FLAG_KEY } from '@/lib/constants'

function getHasSessionFlag(): boolean {
  if (globalThis.window === undefined)
    return false
  return localStorage.getItem(SESSION_FLAG_KEY) === 'true'
}

function setHasSessionFlag(value: boolean): void {
  if (globalThis.window === undefined)
    return
  if (value) {
    localStorage.setItem(SESSION_FLAG_KEY, 'true')
  }
  else {
    localStorage.removeItem(SESSION_FLAG_KEY)
  }
}

function identifyPosthog(user: UserlandBaseUser, subscriptionTier?: string | null) {
  // Super properties persist in the browser: drop the staff-session marker once impersonation ends.
  posthog.unregister('is_impersonating')
  posthog.identify(user.id, {
    role: user.role,
    has_organization: !!user.organization_id,
    is_superuser: user.is_superuser,
  })
  if (user.organization_id) {
    posthog.group('organization', user.organization_id, {
      subscription_tier: subscriptionTier ?? undefined,
    })
    posthog.capture('user_identified', {
      method: 'cookie_auth',
      has_organization: true,
      is_admin: user.role === 'ADMIN',
      is_superuser: user.is_superuser,
      subscription_tier: subscriptionTier ?? undefined,
    })
  }
  else {
    // Groups persist in the browser too: an org-less superuser leaving impersonation would
    // otherwise keep the target org's group (and its org-scoped flags).
    posthog.resetGroups()
  }
}

/**
 * Superuser impersonation: attribute analytics to the admin, not the target user, but evaluate
 * org-scoped feature flags for the target org.
 */
function identifyPosthogWhileImpersonating(
  impersonation: SuperuserImpersonationStatus,
  targetUser: UserlandBaseUser,
  subscriptionTier?: string | null,
) {
  const adminId = impersonation.adminUserId
  if (!adminId)
    return

  // Register before identify so the SDK's $identify event carries the staff-session marker too.
  posthog.register({ is_impersonating: true })
  posthog.identify(adminId, {
    is_impersonating: true,
    impersonated_user_id: targetUser.id,
    impersonated_organization_id: targetUser.organization_id ?? undefined,
    impersonated_role: targetUser.role,
    impersonated_is_superuser: targetUser.is_superuser,
  })
  // Join the target org's group so org-targeted feature flags evaluate as the customer sees them
  // (e.g. accounts-heat-map). Every event in the session carries is_impersonating so customer org
  // metrics can exclude staff sessions.
  if (targetUser.organization_id) {
    posthog.group('organization', targetUser.organization_id, {
      subscription_tier: subscriptionTier ?? undefined,
    })
  }
  else {
    posthog.resetGroups()
  }
  posthog.capture('user_identified', {
    method: 'cookie_auth',
    has_organization: !!targetUser.organization_id,
    // The actor is adminUserId in this path; target-user flags live on the
    // identify call as impersonated_* properties.
    is_admin: true,
    is_superuser: true,
    subscription_tier: subscriptionTier ?? undefined,
    is_impersonating: true,
  })
}

function identifySentry(user: UserlandBaseUser, subscriptionTier?: string | null) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  })
  Sentry.setTag('user.role', user.role ?? 'none')
  Sentry.setTag('user.is_superuser', String(!!user.is_superuser))
  Sentry.setTag('organization.id', user.organization_id ?? 'none')
  Sentry.setContext('organization', {
    id: user.organization_id,
    subscription_tier: subscriptionTier ?? undefined,
  })
}

function identifySentryWhileImpersonating(
  impersonation: SuperuserImpersonationStatus,
  targetUser: UserlandBaseUser,
  subscriptionTier?: string | null,
) {
  const adminId = impersonation.adminUserId
  if (!adminId)
    return

  Sentry.setUser({
    id: adminId,
    email: impersonation.adminEmail ?? undefined,
  })
  Sentry.setTag('user.role', 'impersonation')
  Sentry.setTag('user.is_superuser', 'true')
  Sentry.setTag('organization.id', targetUser.organization_id ?? 'none')
  Sentry.setTag('impersonation.target_user_id', targetUser.id)
  Sentry.setContext('organization', {
    id: targetUser.organization_id,
    subscription_tier: subscriptionTier ?? undefined,
  })
  Sentry.setContext('impersonation', {
    target_user_id: targetUser.id,
    target_user_email: targetUser.email,
  })
}

function clearSentryIdentity() {
  Sentry.setUser({})
  Sentry.setContext('organization', {})
  Sentry.setContext('impersonation', {})
  Sentry.setTag('impersonation.target_user_id', '')
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [hasSession, setHasSession] = useState(getHasSessionFlag)

  const {
    data: user,
    isLoading: isLoadingUser,
    refetch: refetchUser,
    error,
  } = useQuery({
    ...usersCurrentUserAuthUsersMeGetOptions(),
    enabled: hasSession, // Only fetch if we think we have a session
    retry: false, // Don't retry on 401
    retryOnMount: false, // Don't retry when component remounts after initial failure
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 min
  })

  // Clear session on 401 errors. Writing to localStorage is an external side
  // effect belonging in useEffect; state sync happens during render via the
  // "storing information from previous renders" pattern
  // (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // so we don't trip react-hooks/set-state-in-effect.
  const is401 = !!error && 'status' in error && (error as { status?: number }).status === 401
  if (is401 && hasSession) {
    setHasSession(false)
  }

  // Keep the SDK's X-Acting-User-Id header in sync with what this tab's
  // React state believes (SUP-3373). useEffect (not render-time) so a
  // discarded concurrent render can't stomp on a committed render's
  // value — under React 18, render functions can be started, paused, and
  // thrown away, but effects only fire after commit. TanStack refetches
  // scheduled during this render don't actually fire until after commit
  // either, so there's no observable lag.
  useEffect(() => {
    __setActingUserIdInternal(user?.id)
  }, [user?.id])

  useEffect(() => {
    if (is401) {
      setHasSessionFlag(false)
    }
  }, [is401])

  const {
    data: organizationInfoData,
    isLoading: isLoadingMembers,
    refetch: refetchOrganizationMembers,
  } = useQuery({
    ...getOrganizationInfoAppOrganizationOrganizationsInfoGetOptions(),
    enabled: !!user && !!user.organization_id && !user.is_superuser,
  })

  const {
    data: impersonationStatus,
    isLoading: isLoadingImpersonation,
    refetch: refetchImpersonation,
  } = useQuery({
    ...getSuperuserImpersonationStatusAuthSuperuserImpersonationStatusGetOptions(),
    enabled: !!user, // Only fetch when authenticated
    staleTime: 30 * 1000, // Consider fresh for 30 seconds
  })

  const { mutateAsync: loginMutateAsync } = useMutation({
    ...authCookieLoginAuthLoginPostMutation(),
  })

  const { mutateAsync: logoutMutateAsync } = useMutation({
    ...authCookieLogoutAuthLogoutPostMutation(),
  })

  const { mutateAsync: magicLinkCallbackMutateAsync } = useMutation({
    ...magicLinkCallbackAuthMagicLinkCallbackPostMutation(),
  })

  const organizationInfo = organizationInfoData?.organization
  const companyInfo = organizationInfoData?.companyInfo
  const organizationMembers = organizationInfoData?.members
  const entitlements = organizationInfoData?.entitlements

  useEffect(() => {
    if (!user) {
      clearSentryIdentity()
      return
    }
    // Avoid attributing PostHog/Sentry to the impersonated user before we know
    // whether this session is superuser impersonation.
    if (isLoadingImpersonation)
      return

    if (
      impersonationStatus?.isImpersonating
      && impersonationStatus.adminUserId
    ) {
      identifyPosthogWhileImpersonating(
        impersonationStatus,
        user,
        organizationInfo?.subscriptionTier,
      )
      identifySentryWhileImpersonating(
        impersonationStatus,
        user,
        organizationInfo?.subscriptionTier,
      )
    }
    else {
      identifyPosthog(user, organizationInfo?.subscriptionTier)
      identifySentry(user, organizationInfo?.subscriptionTier)
    }
  }, [user, organizationInfo?.subscriptionTier, impersonationStatus, isLoadingImpersonation])

  // Login handler to be exposed via context
  const login = useCallback(async (username: string, password: string) => {
    await loginMutateAsync({
      body: {
        username,
        password,
      },
    })
    setHasSessionFlag(true)
    setHasSession(true)
    // Cookie just rotated to the new user. Drop any stale acting-user id
    // before the /me refetch so the request doesn't claim the previous
    // identity against the fresh cookie (SUP-3373).
    __setActingUserIdInternal()
    const { data: userData } = await refetchUser()
    if (!userData) {
      throw new Error('Failed to fetch user data after login. Please try again.')
    }
    await refetchOrganizationMembers()
    posthog.capture('auth_login_completed', { method: 'password' })
    return userData
  }, [loginMutateAsync, refetchUser, refetchOrganizationMembers])

  // Magic link callback handler to be exposed via context
  const magicLinkCallback = useCallback(async (token: string) => {
    await magicLinkCallbackMutateAsync({
      body: {
        token,
      },
    })
    setHasSessionFlag(true)
    setHasSession(true)
    // Cookie just rotated to the new user. Same rationale as `login`
    // above (SUP-3373).
    __setActingUserIdInternal()
    const { data: userData } = await refetchUser()
    if (!userData) {
      throw new Error('Failed to fetch user data after login. Please try again.')
    }
    await refetchOrganizationMembers()
    posthog.capture('auth_login_completed', { method: 'magic_link' })
    return userData
  }, [magicLinkCallbackMutateAsync, refetchUser, refetchOrganizationMembers])

  // Synchronize the auth context with a cookie-based session that some other
  // backend flow already established (e.g. demoday signup, magic-link
  // onboarding token exchange). These flows set the auth cookie server-side
  // and write the SESSION_FLAG_KEY localStorage entry directly, but the
  // AuthProvider's `hasSession` React state can drift out of sync — which
  // breaks logout, because `setHasSession(false)` becomes a no-op and the
  // subsequent `router.navigate({ to: '/login' })` runs against a stale
  // router context that re-routes the user back to the dashboard. Calling
  // this after a non-credential login keeps both signals (localStorage + the
  // React bulb) in lockstep with the cookie.
  const markSessionEstablished = useCallback(async () => {
    setHasSessionFlag(true)
    setHasSession(true)
    // Cookie just rotated to a new user via a non-credential server flow
    // (demoday signup, onboarding token exchange). Drop any stale
    // acting-user id before the /me refetch so the request doesn't claim
    // the previous identity against the fresh cookie (SUP-3373).
    __setActingUserIdInternal()
    await refetchUser()
    await refetchOrganizationMembers()
  }, [refetchUser, refetchOrganizationMembers])

  // Logout handler to be exposed via context
  const logout = useCallback(async () => {
    try {
      await logoutMutateAsync({})
    }
    finally {
      posthog.capture('auth_logout', {})
      setHasSessionFlag(false)
      setHasSession(false)
      // Clear the SDK header synchronously rather than relying on the
      // useEffect that follows from `user` becoming undefined — that
      // ordering would let any in-flight or about-to-fire request carry
      // the old id against the cleared cookie (SUP-3373). Symmetric with
      // login, magicLinkCallback, and markSessionEstablished.
      __setActingUserIdInternal()
      queryClient.clear()
      posthog.reset()
      clearSentryIdentity()
      await router.navigate({ to: '/login' })
    }
  }, [queryClient, logoutMutateAsync])

  // Refetch helper
  const refetch = useCallback(async () => {
    await refetchUser()
    await refetchOrganizationMembers()
    await refetchImpersonation()
  }, [refetchUser, refetchOrganizationMembers, refetchImpersonation])

  const value: AuthState = {
    user: user ?? undefined,
    isAuthenticated: !!user,
    isLoading: isLoadingUser || isLoadingMembers,
    isSupplycoStaff: user?.is_superuser ?? false,
    organizationId: user?.organization_id ?? undefined,
    organizationName: organizationInfo?.name ?? undefined,
    isOrgAdmin: user?.role === 'ADMIN',
    currentUserRole: user?.role ?? undefined,
    subscriptionTier: organizationInfo?.subscriptionTier ?? undefined,
    isTrialOrg: organizationInfo?.subscriptionTier === 'TRIAL',
    isDistributorOrg: organizationInfo?.subscriptionTier === 'DISTRIBUTOR',
    companyInfo: companyInfo ?? undefined,
    currentAccountCount: entitlements?.currentAccountCount ?? 0,
    maxAccounts: entitlements?.maxAccounts ?? 0,
    isTrialLimitReached: organizationInfo?.subscriptionTier === 'TRIAL' && !!entitlements?.maxAccounts && (entitlements?.currentAccountCount ?? 0) >= entitlements.maxAccounts,
    remainingAccountSlots: Math.max(0, (entitlements?.maxAccounts ?? 0) - (entitlements?.currentAccountCount ?? 0)),
    currentUserCount: organizationMembers?.length ?? 0,
    maxSeats: entitlements?.maxSeats ?? undefined,
    error: error?.message ?? undefined,
    entitlements: entitlements ?? undefined,
    canImportAccounts: entitlements?.canImportAccounts ?? false,
    canDeleteAccounts: entitlements?.canDeleteAccounts ?? false,
    canEditOrgSettings: entitlements?.canEditOrgSettings ?? false,
    canShareWithPartners: entitlements?.canShareWithPartners ?? false,
    canManageDistributors: entitlements?.canManageDistributors ?? false,
    canEditPersonas: entitlements?.canEditPersonas ?? false,
    canEditOutreach: entitlements?.canEditOutreach ?? false,
    canExportAccounts: entitlements?.canExportAccounts ?? false,
    canUseMarketResearch: entitlements?.canUseMarketResearch ?? false,
    canUseIntegrations: entitlements?.canUseIntegrations ?? false,
    canUseOrderInbox: ((entitlements?.canUseOrderInbox ?? false)
      && (user?.email?.toLowerCase().endsWith('@supplyco.ai') ?? false)),
    canUseScoutLeads: entitlements?.canUseScoutLeads ?? false,
    canViewCrm: entitlements?.canViewCrm ?? false,
    canUseAgentRuns: user?.email?.toLowerCase().endsWith('@supplyco.ai') ?? false,
    organizationInfo: organizationInfo ?? undefined,
    organizationMembers: organizationMembers ?? undefined,
    impersonation: impersonationStatus ?? undefined,
    refetch,
    login,
    magicLinkCallback,
    markSessionEstablished,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
